/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
/**
 * These are all of the layers that exist in the canvas world.
 * By default, these are added to the LayerManager as a child to the cameraContainer display node
 * There are two objects in the scene graph that are not managed by the LayerManager, but are implicitly added as part of AbstractApplication:
 * - Stage: Which is the top-most node of the graph and never has a parent (“root”)
 * - World: The container that holds every object that is affected by the Camera (panning, zooming, etc)
 * These objects do not exist in LayerPaths because they are not currently controlled by the LayerManager
 * As a rule, anything added to the `World` object will live in WORLD SPACE.
 * Anything added directly as a child (or the children of such) of Stage will live in SCREEN SPACE.
 * WORLD SPACE = an "infinite" virtual workspace that is a 2D plane.
 * 	For something in World Space to be visible, it must be present in the camera viewport.
 * 	You can access the current WORLD SPACE viewport through the camera: camera.globalView
 * SCREEN SPACE = the projection onto the display device.
 * 	In Screen Space, (0,0) represents the top-left corner of the monitor, whereas (screenWidth, screenHeight) is the bottom right corner
 * 	You can access the screen viewport through the camera: camera.view or camera.getScreen()
 */
export enum LayerPaths {
	// Added this layer for "visual" only elements that draw behind everything else.
	// Objects on this layer cannot have interaction (since the Plan gestures will capture everything instead)
	BACKGROUND_VISUALS = `backgroundVisuals`, // SCREEN SPACE
	BACKGROUND_VISUALS_MILESTONE_AHEAD_BEHIND = `backgroundVisuals/aheadBehind`, // SCREEN SPACE
	BACKGROUND_VISUALS_PULL_SPACE_COLUMNS = `backgroundVisuals/pullSpaceColumns`, // SCREEN SPACE
	BACKGROUND_VISUALS_NON_WORK_COLUMNS = `backgroundVisuals/nonWorkDays`, // SCREEN SPACE
	BACKGROUND_VISUALS_PROMISE_PERIOD = `backgroundVisuals/promisePeriod`, // SCREEN SPACE
	BACKGROUND_VISUALS_ACTIVE_SPACE_COLUMNS = `backgroundVisuals/activeSpaceColumns`, // SCREEN SPACE
	// This is the layer where the "plan gestures" live (Like PlanHold)
	BACKGROUND_GESTURES = `backgroundGestures`, // SCREEN SPACE
	BACKGROUND = 'background', // SCREEN SPACE
	BACKGROUND_TIMELINE = `background/timeline`, // SCREEN SPACE
	BACKGROUND_TIMELINE_TODAY_LINE = `background/timeline/todayLine`, // SCREEN SPACE
	BACKGROUND_TIMELINE_MILESTONE_LINE = `background/timeline/milestoneLine`, // SCREEN SPACE
	BACKGROUND_TIMELINE_ACTIVE_LINE_GHOST = `background/timeline/ghostLine`, // SCREEN SPACE
	BACKGROUND_TIMELINE_ACTIVE_LINE = `background/timeline/activeLine`, // SCREEN SPACE
	WORLD_BACKGROUND = 'worldBackground', // WORLD SPACE
	TICKETS = 'tickets', // WORLD SPACE
	TICKET_DRAG = 'ticketDrag', // WORLD SPACE
	WORLD_ABOVE = 'worldAbove', // WORLD SPACE
	UI = 'ui', // SCREEN SPACE
	UI_GRANULAR_TICKETS = 'granularTickets', // SCREEN SPACE
	UI_TIMELINE_ROOT = `ui/timeline`, // SCREEN SPACE
	UI_TIMELINE_BACKGROUND = `ui/timeline/background`, // SCREEN SPACE
	UI_TIMELINE_TICKETS = `ui/timeline/tickets`, // SCREEN SPACE
	UI_TIMELINE_ACTIVE_SPACE = `ui/timeline/activeSpace`, // SCREEN SPACE
	UI_TIMELINE_PROMISE_PERIOD = `ui/timeline/promisePeriod`, // SCREEN SPACE
	UI_TIMELINE_MILESTONE_LINE = `ui/milestoneLine`, // SCREEN SPACE
	UI_TIMELINE_PULL_SPACE = `ui/pullSpace`, // SCREEN SPACE

	BLOCKER = 'blocker', // SCREEN SPACE
	BLOCKER_PRINT_EXPORT = `blocker/printExport`, // SCREEN SPACE
}
